import { useState } from "react";

export default function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      setSubmitted(true);
      form.reset();
      setCharCount(0);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="py-24 px-6 bg-white">
      <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
        {/* Left info panel */}
        <div className="lg:col-span-2">
          <span className="inline-flex items-center gap-2 text-[#C9A961] text-xs font-semibold uppercase tracking-[0.25em] mb-5">
            <span className="w-8 h-px bg-[#C9A961]" />
            Get in Touch
          </span>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-bold text-[#2B2D2F] leading-tight mb-6">
            Send Us a Message
          </h2>
          <p className="text-[#6B6B6B] text-base leading-relaxed mb-10">
            Complete the form and a member of our team will review your inquiry and respond within 2–3 business days. All conversations are handled with professionalism and discretion.
          </p>

          <div className="space-y-6 mb-10">
            {[
              {
                icon: "ri-map-pin-2-line",
                title: "Nairobi & Fort Lauderdale",
                desc: "Harambee Global Ventures is anchored in Nairobi, Kenya and identifies Fort Lauderdale, Florida among its primary markets in the business plan.",
              },
              {
                icon: "ri-global-line",
                title: "Global Engagement",
                desc: "We engage with the African Diaspora and strategic partners across North America, Europe, East Africa, and beyond.",
              },
              {
                icon: "ri-mail-line",
                title: "Professional Response",
                desc: "Every inquiry is reviewed by our team. We are committed to thoughtful, timely, and personalized responses.",
              },
              {
                icon: "ri-lock-2-line",
                title: "Confidential & Secure",
                desc: "Your information is handled with the highest level of discretion. We never share your details with third parties.",
              },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 flex-shrink-0">
                  <i className={`${item.icon} text-[#1A4D3C] text-base`} />
                </div>
                <div>
                  <div className="font-semibold text-[#2B2D2F] text-sm mb-0.5">{item.title}</div>
                  <div className="text-[#6B6B6B] text-xs leading-relaxed">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <img
            src="/assets/images/site/contact-form-img-001-560x320.jpg"
            alt="Harambee Global Ventures professional team Nairobi"
            className="w-full h-[220px] object-cover object-top rounded-lg"
          />
        </div>

        {/* Right form */}
        <div className="lg:col-span-3 bg-[#F8F6F3] rounded-lg p-8 md:p-10 border border-[#E8E4DC]">
          {submitted ? (
            <div className="text-center py-14">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 mx-auto mb-5">
                <i className="ri-checkbox-circle-line text-[#1A4D3C] text-3xl" />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#2B2D2F] mb-3">
                Message Received
              </h3>
              <p className="text-[#6B6B6B] text-sm leading-relaxed max-w-sm mx-auto">
                Thank you for reaching out to Harambee Global Ventures. Our team will review your message and respond within 2–3 business days. We look forward to connecting with you.
              </p>
            </div>
          ) : (
            <>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#2B2D2F] mb-1">
                Contact Harambee Global Ventures
              </h3>
              <p className="text-[#6B6B6B] text-sm mb-7">Fields marked with * are required.</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="Your full name"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone_number"
                      placeholder="+1 (555) 000-0000"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                      Organization / Company
                    </label>
                    <input
                      type="text"
                      name="organization"
                      placeholder="Your organization (if applicable)"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                    Inquiry Type *
                  </label>
                  <select
                    name="inquiry_type"
                    required
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select your inquiry type</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Trading Academy">Trading Academy Questions</option>
                    <option value="Advisory Consultation">Advisory Consultation</option>
                    <option value="Partnership Opportunity">Partnership Opportunity</option>
                    <option value="Media or Speaking Request">Media or Speaking Request</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                    Message *
                    <span className="ml-2 text-[#ABABAB] font-normal normal-case tracking-normal">
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    placeholder="Please share what you are looking for and how we can best assist you..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors resize-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                    Preferred Contact Method
                  </label>
                  <select
                    name="preferred_contact_method"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select preferred method</option>
                    <option value="Email">Email</option>
                    <option value="Phone Call">Phone Call</option>
                    <option value="Video Call">Video Call (Zoom / Teams)</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="No Preference">No Preference</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 rounded-md bg-[#1A4D3C] text-white font-semibold text-sm hover:bg-[#0D2E1E] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>

                <p className="text-[#ABABAB] text-xs text-center leading-relaxed">
                  By submitting this form, you agree to be contacted by the Harambee Global Ventures team. We respect your privacy and will never share your information with third parties.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
